/**
 * <fitment-check> — the personalized "does this part fit YOUR car" verdict on the product page.
 *
 * Progressive enhancement layered over the server-rendered vehicle-fitment chip list. The block
 * emits the product's vehicle (chassis) collection handles plus a universal flag on this element;
 * here we read the shopper's ACTIVE car from My Garage (the shared garage-store) and reveal a
 * verdict banner above the list. Precedence:
 *   - universal-fit part                       -> "Universal fit" (vehicle-neutral, short-circuits)
 *   - car set and the part is listed for it    -> "Fits your 2018 Subaru WRX" (success)
 *   - car set, part listed for other vehicles  -> "Not listed for your 2018 Subaru WRX"
 *   - car set, part in no chassis collection    -> "Fitment not confirmed" (pending)
 *   - no car, but the part is vehicle-specific  -> a prompt that opens the garage
 *   - no car and nothing to confirm             -> nothing (banner stays hidden)
 *
 * The matching chip is highlighted on a fit. Everything re-runs on `vehicle:change`, so switching
 * cars in the garage updates the verdict live (and across tabs). With no JS the chip list stands
 * on its own.
 *
 * Fitment truth is collection membership: the same chassis collections the garage and parts-finder
 * route to (e.g. /collections/subaru-wrx-vb), and the universal-fit collection for vehicle-neutral
 * parts, so the verdict and the chip list can never disagree.
 */

import { getActive } from '@theme/garage-store';

class FitmentCheck extends HTMLElement {
  /** @type {HTMLElement | null} */ #verdict = null;
  /** @type {HTMLButtonElement | null} */ #prompt = null;
  /** @type {string[]} */ #handles = [];
  #universal = false;
  #fitsTpl = '';
  #unconfirmedTpl = '';
  #askHref = '';

  connectedCallback() {
    this.#verdict = this.querySelector('.product-fitment__verdict');
    this.#prompt = this.querySelector('.product-fitment__verdict-prompt');
    this.#handles = (this.getAttribute('data-vehicle-handles') || '')
      .split(/\s+/)
      .map((h) => h.toLowerCase())
      .filter(Boolean);
    this.#universal = this.getAttribute('data-universal') === 'true';
    this.#fitsTpl = this.getAttribute('data-fits-template') || '%s';
    this.#unconfirmedTpl = this.getAttribute('data-unconfirmed-template') || '%s';
    this.#askHref = this.getAttribute('data-ask-href') || '';

    if (!this.#verdict) return;

    this.#prompt?.addEventListener('click', this.#openGarage);
    document.addEventListener('vehicle:change', this.#render);
    this.#render();
  }

  disconnectedCallback() {
    document.removeEventListener('vehicle:change', this.#render);
  }

  #render = () => {
    const active = getActive();
    const activeHandle = active ? this.#handleFromUrl(active.url) : null;

    // Precedence (per the universal-fit spec): a universal part is a positive, vehicle-neutral
    // state and short-circuits before any "fits / not listed" logic. Otherwise the active car
    // drives the verdict; with no chassis collection at all we can only say "not confirmed".
    let state;
    if (this.#universal) {
      state = 'universal';
    } else if (active && activeHandle && this.#handles.includes(activeHandle)) {
      state = 'fits';
    } else if (active && activeHandle) {
      state = this.#handles.length > 0 ? 'unconfirmed' : 'pending';
    } else {
      // No car saved. Prompt only when picking one could actually resolve fitment.
      state = this.#handles.length > 0 ? 'prompt' : '';
    }

    if (!state) {
      this.#verdict.hidden = true;
      delete this.dataset.state;
      this.#highlightChip(null);
      return;
    }

    if (state === 'fits') {
      this.#setText('.js-fitment-fits', this.#fitsTpl.replace('%s', active.label || ''));
    } else if (state === 'unconfirmed') {
      this.#setText('.js-fitment-unconfirmed', this.#unconfirmedTpl.replace('%s', active.label || ''));
      this.#setAskHref(active.label);
    } else if (state === 'pending') {
      this.#setAskHref(active.label);
    }

    this.dataset.state = state;
    this.#verdict.hidden = false;
    this.#highlightChip(state === 'fits' ? activeHandle : null);
  };

  /** Open the header "My Garage" modal so the shopper can pick a car without leaving the page. */
  #openGarage = () => {
    const triggers = [...document.querySelectorAll('.vehicle-garage__trigger')];
    const target = triggers.find((t) => t.offsetParent !== null) || triggers[0];
    if (target instanceof HTMLElement) target.click();
  };

  /** "/collections/subaru-wrx-vb" (or an absolute URL) -> "subaru-wrx-vb". */
  #handleFromUrl(url) {
    if (!url) return null;
    let path = String(url);
    try {
      path = new URL(url, window.location.origin).pathname;
    } catch {
      path = path.split('?')[0].split('#')[0];
    }
    const seg = path.replace(/\/+$/, '').split('/').pop();
    return seg ? seg.toLowerCase() : null;
  }

  #setText(selector, text) {
    const el = this.#verdict?.querySelector(selector);
    if (el) el.textContent = text;
  }

  /**
   * Point the "Ask about fitment" links at a pre-filled email that already names
   * the shopper's selected vehicle. The server emits the body with a %%VEHICLE%%
   * token (URL-encoded as %25%25VEHICLE%25%25); swap in the encoded car label.
   */
  #setAskHref(label) {
    if (!this.#askHref || !label) return;
    const href = this.#askHref.replace('%25%25VEHICLE%25%25', encodeURIComponent(label));
    this.#verdict?.querySelectorAll('.product-fitment__verdict-link').forEach((a) => {
      a.setAttribute('href', href);
    });
  }

  #highlightChip(handle) {
    this.querySelectorAll('.product-fitment__chip').forEach((chip) => {
      const match = !!handle && chip.dataset.handle === handle;
      chip.classList.toggle('product-fitment__chip--active', match);
      if (match) chip.setAttribute('aria-current', 'true');
      else chip.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('fitment-check')) {
  customElements.define('fitment-check', FitmentCheck);
}
